import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { KPICard } from "@/components/collections/kpi-card";
import { LogCallModal, SendEmailModal, ReassignUserModal, UpdateStatusModal } from "@/components/collections/action-modals";

import { CreatePromiseDrawer } from "@/components/collections/create-promise-drawer";
import { RecordPromiseDialog, type RecordPromiseData } from "@/components/collections/record-promise-dialog";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import {
  Search,
  Phone,
  Mail,
  FileText,
  Clock,
  Inbox,
  AlertTriangle,
  PoundSterling,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings,
  FileSpreadsheet,
  Plus,
  X,
  HandshakeIcon,
  Info,
  UserCog,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";
import { useCaseContacts } from "@/hooks/useCaseDetailData";
import { useCollectionCasesPageData, useCollectionCaseTotals, type GetCollectionCasesParams } from "@/hooks/useCollectionCasesGraphQL";
import { type CollectionCase, type PromiseToPay } from "@/types/collections";
import { exportCollectionCasesToExcel } from "@/lib/collections-export-utils";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";
import { useUsersList } from "@/hooks/useUsersData";

const DEFAULT_QUEUE_COLUMNS: ColumnDefinition[] = [
  { id: 'select', label: 'Select', visible: true, order: 0 },
  { id: 'priority', label: 'Priority', visible: true, order: 1 },
  { id: 'caseId', label: 'Case ID', visible: true, order: 2 },
  { id: 'customerName', label: 'Customer', visible: true, order: 3 },
  { id: 'amount', label: 'Amount', visible: true, order: 4 },
  { id: 'daysInCollection', label: 'Days', visible: true, order: 5 },
  { id: 'lastAction', label: 'Last Action', visible: true, order: 6 },
  { id: 'nextAction', label: 'Next Action', visible: true, order: 7 },
  { id: 'nextActionDate', label: 'Action Due Date', visible: true, order: 8 },
  { id: 'status', label: 'Status', visible: true, order: 9 },
  { id: 'strategy', label: 'Collection Strategy', visible: true, order: 10 },
  { id: 'stage', label: 'Stage', visible: true, order: 11 },
  { id: 'customerId', label: 'Customer ID', visible: false, order: 12 },
  { id: 'customerEmail', label: 'Email', visible: false, order: 13 },
  { id: 'customerPhone', label: 'Phone', visible: false, order: 14 },
  { id: 'assignedTo', label: 'Assigned To', visible: false, order: 15 },
  { id: 'createdAt', label: 'Created Date', visible: false, order: 16 },
  { id: 'updatedAt', label: 'Last Updated', visible: false, order: 17 },
];

// Helper function to get the latest action for a case
const getLatestAction = (caseItem: CollectionCase) => {
  if (caseItem.lastAction) {
    return {
      type: caseItem.lastAction.type,
      description: caseItem.lastAction.description,
      performedBy: caseItem.lastAction.performedBy,
      date: caseItem.lastAction.date,
    };
  }
  return null;
};

export default function CollectionsQueue() {
  console.log('[CollectionsQueue] Component rendering...');
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const filterParam = searchParams.get("filter");
  
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active"); // Default to Active cases only
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assignedUserFilter, setAssignedUserFilter] = useState("all");
  const [selectedCase, setSelectedCase] = useState<CollectionCase | null>(null);
  const [selectedCaseIds, setSelectedCaseIds] = useState<Set<string>>(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Modal states
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [createPromiseOpen, setCreatePromiseOpen] = useState(false);
  const [recordPromiseDialogOpen, setRecordPromiseDialogOpen] = useState(false);
  const [reassignUserOpen, setReassignUserOpen] = useState(false);
  const [updateStatusOpen, setUpdateStatusOpen] = useState(false);

  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

    // Column preferences
    const { columns, visibleColumns, savePreferences, resetToDefaults } = useScreenColumnPreferences('collections-queue', DEFAULT_QUEUE_COLUMNS);

  // Sorting state - Default sort: Action Due Date (asc), then Priority (desc)
  type SortField = "priority" | "caseId" | "customerName" | "amount" | "daysInCollection" | "nextAction" | "nextActionDate" | "status" | "strategy" | "stage" | "customerId" | "customerEmail" | "customerPhone" | "assignedTo" | "createdAt" | "updatedAt";
  type SortDirection = "asc" | "desc" | null;
  const [sortField, setSortField] = useState<SortField | null>("nextActionDate"); // Default primary sort
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc"); // Ascending - oldest dates first

  // Build query params for server-side filtering, sorting, and pagination
  const queryParams: GetCollectionCasesParams = useMemo(() => {
    const params: GetCollectionCasesParams = {
      page: currentPage,
      pageSize: itemsPerPage,
      sortBy: sortField ?? "nextActionDate",
      sortOrder: sortDirection ?? "asc",
    };
    if (searchQuery) params.search = searchQuery;
    if (statusFilter && statusFilter !== "all") params.status = statusFilter;
    if (priorityFilter && priorityFilter !== "all") params.priority = priorityFilter;
    if (assignedUserFilter && assignedUserFilter !== "all") {
      const userId = Number(assignedUserFilter);
      if (!isNaN(userId)) params.assignedToUserId = userId;
    }
    return params;
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, priorityFilter, assignedUserFilter, sortField, sortDirection]);

  // GraphQL-backed hook: server-side filtering, sorting, and pagination
  const { cases, pagination, isLoading } = useCollectionCasesPageData(queryParams);

  // Server-side totals for KPI cards (applies to all pages, not just the current page)
  const { data: totals, isLoading: isTotalsLoading } = useCollectionCaseTotals(queryParams);
  
  // Fetch system users for the Users filter dropdown (respects demo/live mode)
  const { data: systemUsers = [] } = useUsersList();
  const activeUsers = useMemo(() =>
    (systemUsers || []).filter(user => user?.status === 'active'),
    [systemUsers]
  );

  // Get contacts for the selected case - only fetch when we have a selected case
  const { data: contactsData = [] } = useCaseContacts(selectedCase?.customerId || undefined);

  // Handle URL filter parameter
  useEffect(() => {
    if (filterParam === "action-due-today") {
      // When coming from Homepage "Cases Due" metric, show all statuses
      setStatusFilter("all");
    }
  }, [filterParam]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, priorityFilter, assignedUserFilter, filterParam]);

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> back to default
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        // Reset to default sort (Action Due Date ascending)
        setSortField("nextActionDate");
        setSortDirection("asc");
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  // Build sorted list of active system users for the Users filter dropdown
  const userFilterOptions = useMemo(() => {
    return activeUsers
      .map(user => ({
        id: user.id,
        displayName: `${user.firstName} ${user.lastName}`,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [activeUsers]);

  // With server-side filtering/sorting/pagination, cases from the hook are already
  // filtered, sorted, and paginated. Apply only the local "action-due-today" filter.
  const filteredCases = useMemo(() => {
    if (filterParam === "action-due-today") {
      return cases.filter((caseItem) => {
        const daysDiff = differenceInDays(new Date(caseItem.nextActionDate), new Date());
        return daysDiff === 0;
      });
    }
    return cases;
  }, [cases, filterParam]);

  // Server-side pagination values
  const totalPages = pagination.totalPages;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, pagination.totalItems);
  const paginatedCases = filteredCases;

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('ellipsis-start');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  // KPI values from the server-side totals query (across all pages)
  const kpis = useMemo(() => ({
    myCases: totals?.totalCount ?? 0,
    promisedCases: totals?.promisedCount ?? 0,
    highPriority: totals?.highPriorityCount ?? 0,
    dueToday: totals?.dueTodayCount ?? 0,
    totalOutstanding: totals?.totalOutstanding ?? 0,
  }), [totals]);

  const handleViewCaseDetails = (caseItem: CollectionCase) => {
    // Navigate to full-screen case details page
    navigate(`/collections/queue/case/${caseItem.caseId}`, {
      state: { from: "queue" }
    });
  };

    const handleAction = (action: string, caseItem: CollectionCase) => {
      setSelectedCase(caseItem);
      switch (action) {
        case "call":
          setLogCallOpen(true);
          break;
        case "email":
          setSendEmailOpen(true);
          break;
        case "promise":
          setRecordPromiseDialogOpen(true);
          break;
        default:
          break;
      }
    };
    // handleAction is used in the table row actions
    void handleAction;

  // Checkbox selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredCases.map((c) => c.id));
      setSelectedCaseIds(allIds);
    } else {
      setSelectedCaseIds(new Set());
    }
  };

  const handleSelectCase = (caseId: string, checked: boolean) => {
    const newSelected = new Set(selectedCaseIds);
    if (checked) {
      newSelected.add(caseId);
    } else {
      newSelected.delete(caseId);
    }
    setSelectedCaseIds(newSelected);
  };

  // Bulk action handlers
  const handleBulkAction = (action: string) => {
    if (selectedCaseIds.size === 0) return;
    
    // For single selection, use the first selected case
    const firstSelectedId = Array.from(selectedCaseIds)[0];
    const firstCase = cases.find((c) => c.id === firstSelectedId);
    
    if (firstCase) {
      setSelectedCase(firstCase);
      switch (action) {
        case "call":
          setLogCallOpen(true);
          break;
        case "email":
          setSendEmailOpen(true);
          break;
        case "promise":
          setRecordPromiseDialogOpen(true);
          break;
        case "reassign":
          setReassignUserOpen(true);
          break;
        case "updateStatus":
          setUpdateStatusOpen(true);
          break;
        default:
          break;
      }
    }
  };

  const selectedCases = useMemo(() => {
    return cases.filter((c) => selectedCaseIds.has(c.id));
  }, [cases, selectedCaseIds]);

  // Handler for recording a promise from the dialog
  const handleRecordPromiseFromQueue = (promiseData: RecordPromiseData) => {
    if (!selectedCase) return;

    // Update case status to "Promised" and add promise data
    const dataStore = getGlobalDataStore();
    const newPromise: PromiseToPay = {
      id: `PRM-${Date.now()}`,
      customerId: selectedCase.customerId,
      customerName: selectedCase.customerName,
      customerAccountNumber: `ACC-${selectedCase.customerId.padStart(3, '0')}`,
      customerEmail: selectedCase.customerEmail,
      customerPhone: selectedCase.customerPhone,
      caseId: selectedCase.id,
      caseNumber: selectedCase.caseId,
      promiseDate: new Date(promiseData.promiseDate),
      promisedAmount: selectedCase.amount, // Use the full case amount
      status: 'Active',
      confirmationMethod: promiseData.confirmationMethod as 'Phone' | 'Email' | 'Portal',
      createdBy: 'Current User',
      createdAt: new Date(),
      notes: promiseData.notes,
      invoices: [],
      assignedUserId: 0,
      assignedUserName: 'Current User',
      isAtRisk: false,
    };

    dataStore.updateCase(selectedCase.caseId, {
      status: 'Promised',
      currentPromise: newPromise,
      promiseHistory: [...(selectedCase.promiseHistory || []), newPromise],
      promiseCount: (selectedCase.promiseCount || 0) + 1,
      nextAction: 'Follow up on promise',
      nextActionDate: new Date(promiseData.promiseDate),
    });

    // Force re-render by updating state
    window.location.reload();
  };

  // Handler for reassigning user
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleReassignUserSave = (userId: string) => {
    // In a real app, this would call an API to reassign the cases
    setSelectedCaseIds(new Set());
  };

  // Handler for updating status
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUpdateStatusSave = (status: string, notes: string) => {
    // In a real app, this would call an API to update the status
    setSelectedCaseIds(new Set());
  };

  const isAllSelected = filteredCases.length > 0 && selectedCaseIds.size === filteredCases.length;
  const isSomeSelected = selectedCaseIds.size > 0 && selectedCaseIds.size < filteredCases.length;

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "On Hold":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "Promised":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "Escalated":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "Closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "";
    }
  };

  const isOverdue = (date: Date) => {
    return differenceInDays(new Date(), new Date(date)) > 0;
  };

  const isDueToday = (date: Date) => {
    return differenceInDays(new Date(date), new Date()) === 0;
  };

  // Export handler - current view
  const handleExportToExcel = async () => {
    if (filteredCases.length === 0) {
      return;
    }

    setIsExporting(true);
    try {
      exportCollectionCasesToExcel(filteredCases);
    } catch (error) {
      console.error("Error exporting cases:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Export handler - all columns
  const handleExportAllColumns = async () => {
    if (filteredCases.length === 0) {
      return;
    }

    setIsExporting(true);
    try {
      exportCollectionCasesToExcel(filteredCases, `my-cases-all-columns-${new Date().toISOString().split("T")[0]}.csv`);
    } catch (error) {
      console.error("Error exporting cases:", error);
    } finally {
      setIsExporting(false);
    }
  };


  const renderColumnHeader = (colId: string): React.ReactNode => {
    switch (colId) {
      case 'select':
        return (
          <TableHead key={colId} className="w-[50px]">
            <Checkbox
              checked={isAllSelected}
              onCheckedChange={handleSelectAll}
              aria-label="Select all cases"
              className={cn(isSomeSelected && !isAllSelected && "data-[state=checked]:bg-primary")}
            />
          </TableHead>
        );
      case 'priority':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("priority")}>
              <span>Priority</span>
              {getSortIcon("priority")}
            </Button>
          </TableHead>
        );
      case 'caseId':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("caseId")}>
              <span>Case ID</span>
              {getSortIcon("caseId")}
            </Button>
          </TableHead>
        );
      case 'customerName':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("customerName")}>
              <span>Customer</span>
              {getSortIcon("customerName")}
            </Button>
          </TableHead>
        );
      case 'amount':
        return (
          <TableHead key={colId} className="text-right">
            <Button variant="ghost" size="sm" className="-mr-3 h-8 font-bold data-[state=open]:bg-accent ml-auto flex" onClick={() => handleSort("amount")}>
              <span>Amount</span>
              {getSortIcon("amount")}
            </Button>
          </TableHead>
        );
      case 'daysInCollection':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("daysInCollection")}>
              <span>Days</span>
              {getSortIcon("daysInCollection")}
            </Button>
          </TableHead>
        );
      case 'lastAction':
        return <TableHead key={colId} className="font-bold">Last Action</TableHead>;
      case 'nextAction':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("nextAction")}>
              <span>Next Action</span>
              {getSortIcon("nextAction")}
            </Button>
          </TableHead>
        );
      case 'nextActionDate':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("nextActionDate")}>
              <span>Action Due Date</span>
              {getSortIcon("nextActionDate")}
            </Button>
          </TableHead>
        );
      case 'status':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("status")}>
              <span>Status</span>
              {getSortIcon("status")}
            </Button>
          </TableHead>
        );
      case 'strategy':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("strategy")}>
              <span>Collection Strategy</span>
              {getSortIcon("strategy")}
            </Button>
          </TableHead>
        );
      case 'stage':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("stage")}>
              <span>Stage</span>
              {getSortIcon("stage")}
            </Button>
          </TableHead>
        );
      case 'customerId':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("customerId")}>
              <span>Customer ID</span>
              {getSortIcon("customerId")}
            </Button>
          </TableHead>
        );
      case 'customerEmail':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("customerEmail")}>
              <span>Email</span>
              {getSortIcon("customerEmail")}
            </Button>
          </TableHead>
        );
      case 'customerPhone':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("customerPhone")}>
              <span>Phone</span>
              {getSortIcon("customerPhone")}
            </Button>
          </TableHead>
        );
      case 'assignedTo':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("assignedTo")}>
              <span>Assigned To</span>
              {getSortIcon("assignedTo")}
            </Button>
          </TableHead>
        );
      case 'createdAt':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("createdAt")}>
              <span>Created Date</span>
              {getSortIcon("createdAt")}
            </Button>
          </TableHead>
        );
      case 'updatedAt':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 font-bold data-[state=open]:bg-accent" onClick={() => handleSort("updatedAt")}>
              <span>Last Updated</span>
              {getSortIcon("updatedAt")}
            </Button>
          </TableHead>
        );
      default:
        return null;
    }
  };

  const renderColumnCell = (colId: string, caseItem: CollectionCase): React.ReactNode => {
    switch (colId) {
      case 'select':
        return (
          <TableCell key={colId}>
            <Checkbox
              checked={selectedCaseIds.has(caseItem.id)}
              onCheckedChange={(checked) => handleSelectCase(caseItem.id, checked as boolean)}
              aria-label={`Select case ${caseItem.caseId}`}
            />
          </TableCell>
        );
      case 'priority':
        return (
          <TableCell key={colId}>
            <Badge variant={getPriorityBadgeVariant(caseItem.priority)}>
              {caseItem.priority}
            </Badge>
          </TableCell>
        );
      case 'caseId':
        return (
          <TableCell key={colId} className="font-medium">
            <Button
              variant="link"
              className="h-auto p-0 font-medium hover:underline"
              onClick={() => handleViewCaseDetails(caseItem)}
            >
              {caseItem.caseId}
            </Button>
          </TableCell>
        );
      case 'customerName':
        return <TableCell key={colId}>{caseItem.customerName}</TableCell>;
      case 'amount':
        return (
          <TableCell key={colId} className="text-right font-medium">
            £{caseItem.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TableCell>
        );
      case 'daysInCollection':
        return (
          <TableCell key={colId}>
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span className="text-sm">{caseItem.daysInCollection}d</span>
            </div>
          </TableCell>
        );
      case 'lastAction': {
        const latestAction = getLatestAction(caseItem);
        if (!latestAction) {
          return (
            <TableCell key={colId}>
              <span className="text-xs text-muted-foreground">—</span>
            </TableCell>
          );
        }
        return (
          <TableCell key={colId}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center">
                    <Info className="h-4 w-4 text-muted-foreground hover:text-primary cursor-help" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div className="space-y-2">
                    <p className="font-medium text-sm">Latest Action</p>
                    <div className="space-y-1">
                      <p className="text-xs font-medium capitalize">{latestAction.type}</p>
                      <p className="text-xs text-muted-foreground">{latestAction.description}</p>
                      {latestAction.performedBy && (
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">By:</span> {latestAction.performedBy}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {format(latestAction.date, "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        );
      }
      case 'nextAction':
        return (
          <TableCell key={colId}>
            <p className="text-sm">{caseItem.nextAction}</p>
          </TableCell>
        );
      case 'nextActionDate':
        return (
          <TableCell key={colId}>
            <p
              className={cn(
                "text-sm",
                isOverdue(caseItem.nextActionDate)
                  ? "text-red-600 dark:text-red-400 font-medium"
                  : isDueToday(caseItem.nextActionDate)
                  ? "text-amber-600 dark:text-amber-400 font-medium"
                  : "text-muted-foreground"
              )}
            >
              {format(new Date(caseItem.nextActionDate), "dd/MM/yyyy")}
            </p>
          </TableCell>
        );
      case 'status':
        return (
          <TableCell key={colId}>
            <div className="flex flex-col gap-1">
              <Badge className={getStatusBadgeColor(caseItem.status)}>
                {caseItem.status}
              </Badge>
              {caseItem.status === 'Promised' && caseItem.currentPromise && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-xs text-muted-foreground">
                        Due: {format(new Date(caseItem.currentPromise.promiseDate), "MMM d")}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="space-y-1">
                        <p className="font-medium">Promise Details</p>
                        <p className="text-xs">Amount: £{caseItem.currentPromise.promisedAmount.toLocaleString()}</p>
                        <p className="text-xs">Due: {format(new Date(caseItem.currentPromise.promiseDate), "MMM d, yyyy")}</p>
                        <p className="text-xs">Method: {caseItem.currentPromise.confirmationMethod}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </TableCell>
        );
      case 'strategy':
        return (
          <TableCell key={colId}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-sm truncate max-w-[200px] block">
                    {caseItem.strategy.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{caseItem.strategy.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        );
      case 'stage':
        return (
          <TableCell key={colId}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 min-w-[140px]">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[100px]">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${(caseItem.strategyPhase.currentPhase / caseItem.strategyPhase.totalPhases) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {caseItem.strategyPhase.currentPhase}/{caseItem.strategyPhase.totalPhases}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-medium">{caseItem.strategyPhase.phaseName}</p>
                  <p className="text-xs text-muted-foreground">
                    Phase {caseItem.strategyPhase.currentPhase} of {caseItem.strategyPhase.totalPhases}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        );
      case 'customerId':
        return <TableCell key={colId}>{caseItem.customerId}</TableCell>;
      case 'customerEmail':
        return <TableCell key={colId}>{caseItem.customerEmail}</TableCell>;
      case 'customerPhone':
        return <TableCell key={colId}>{caseItem.customerPhone}</TableCell>;
      case 'assignedTo':
        return <TableCell key={colId}>{caseItem.assignedTo ?? '\u2014'}</TableCell>;
      case 'createdAt':
        return <TableCell key={colId}>{format(new Date(caseItem.createdAt), "dd/MM/yyyy")}</TableCell>;
      case 'updatedAt':
        return <TableCell key={colId}>{format(new Date(caseItem.updatedAt), "dd/MM/yyyy")}</TableCell>;
      default:
        return null;
    }
  };

  // Safety check - if cases failed to load, show error
  if (!cases || cases.length === 0) {
    console.warn('[CollectionsQueue] No cases available');
  }

  console.log('[CollectionsQueue] Rendering with', cases?.length || 0, 'cases');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Collections Queue</h1>
          </div>
          <p className="text-muted-foreground">Manage your assigned collection cases</p>
        </div>
        <Button onClick={() => navigate('/collections/queue/create', { state: { from: 'queue' } })} className="gap-2">
          <Plus className="h-4 w-4" />
          Create New Case
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <KPICard
          title="My Cases"
          value={kpis.myCases}
          subtitle="Total cases matching filters"
          icon={Inbox}
          colorScheme="blue"
          isLoading={isTotalsLoading}
        />
        <KPICard
          title="Promised"
          value={kpis.promisedCases}
          subtitle="Payment commitments"
          icon={HandshakeIcon}
          colorScheme="blue"
          isLoading={isTotalsLoading}
        />
        <KPICard
          title="High Priority"
          value={kpis.highPriority}
          subtitle="Requiring immediate attention"
          icon={AlertTriangle}
          colorScheme="red"
          isLoading={isTotalsLoading}
        />
        <KPICard
          title="Due Today"
          value={kpis.dueToday}
          subtitle="Actions scheduled for today"
          icon={Calendar}
          colorScheme="amber"
          isLoading={isTotalsLoading}
        />
        <KPICard
          title="Total Outstanding"
          value={`£${kpis.totalOutstanding.toLocaleString()}`}
          subtitle="Sum of outstanding amounts"
          icon={PoundSterling}
          colorScheme="purple"
          isLoading={isTotalsLoading}
        />
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex-shrink-0">
              <CardTitle>My Cases</CardTitle>
              <CardDescription>View and manage your assigned collection cases</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-1 justify-end">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by case ID, customer name or account number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Promised">Promised</SelectItem>
                  <SelectItem value="Escalated">Escalated</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={assignedUserFilter} onValueChange={setAssignedUserFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Assigned User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {userFilterOptions.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={isExporting || filteredCases.length === 0}
                    className="h-8 w-8"
                  >
                    <FileSpreadsheet className={cn("h-4 w-4", isExporting && "animate-pulse")} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportToExcel}>
                    Export Current View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllColumns}>
                    Export All Columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      aria-label="Customize Columns"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Customize Columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent>

          {/* Cases Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map(col => renderColumnHeader(col.id))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="h-64 text-center">
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Loading cases...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && paginatedCases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="text-center text-muted-foreground">
                      {filteredCases.length === 0 ? "No cases found" : "No cases on this page"}
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && paginatedCases.map((caseItem) => (
                  <TableRow 
                    key={caseItem.id}
                    className={cn(
                      selectedCaseIds.has(caseItem.id) && "bg-muted/50"
                    )}
                  >
                    {visibleColumns.map(col => renderColumnCell(col.id, caseItem))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {pagination.totalItems > 0 && (
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Items per page selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">
                  Showing {startIndex + 1}-{Math.min(endIndex, pagination.totalItems)} of {pagination.totalItems} cases
                </span>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        className={cn(
                          currentPage === 1 && "pointer-events-none opacity-50",
                          "cursor-pointer"
                        )}
                      />
                    </PaginationItem>
                    
                    {getPageNumbers().map((page, index) => (
                      <PaginationItem key={`${page}-${index}`}>
                        {typeof page === 'number' ? (
                          <PaginationLink
                            onClick={() => setCurrentPage(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        ) : (
                          <PaginationEllipsis />
                        )}
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        className={cn(
                          currentPage === totalPages && "pointer-events-none opacity-50",
                          "cursor-pointer"
                        )}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Floating Quick Actions Menu */}
      {selectedCaseIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedCaseIds.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedCaseIds.size === 1 ? "case" : "cases"} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                    <span>
                      Total: <span className="font-semibold">£{selectedCases.reduce((sum, c) => sum + c.amount, 0).toLocaleString()}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {selectedCaseIds.size === 1 && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleBulkAction("call")}
                      className="gap-2"
                    >
                      <Phone className="h-4 w-4" />
                      Log Call
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleBulkAction("email")}
                      className="gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      Send Email
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleBulkAction("promise")}
                      className="gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Record Promise
                    </Button>
                  </>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleBulkAction("reassign")}
                  className="gap-2"
                >
                  <UserCog className="h-4 w-4" />
                  Reassign User
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleBulkAction("updateStatus")}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Update Status
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCaseIds(new Set())}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modals */}
      {selectedCase && (
        <>
          <LogCallModal
            open={logCallOpen}
            onOpenChange={setLogCallOpen}
            customerName={selectedCase.customerName}
            customerPhone="(555) 123-4567"
          />
          <SendEmailModal
            open={sendEmailOpen}
            onOpenChange={setSendEmailOpen}
            customerName={selectedCase.customerName}
            customerEmail="customer@example.com"
            customerId={selectedCase.customerId}
          />
        </>
      )}

      {/* Create Promise Drawer */}
      <CreatePromiseDrawer
        open={createPromiseOpen}
        onOpenChange={setCreatePromiseOpen}
        prePopulatedCustomer={selectedCase ? {
          customerId: selectedCase.customerId,
          customerName: selectedCase.customerName,
          accountNumber: `ACC-${selectedCase.customerId.padStart(3, '0')}`,
        } : undefined}
        prePopulatedTransactions={selectedCase ? [
          {
            id: `${selectedCase.id}-1`,
            headerReference: `INV-2024-${selectedCase.customerId.padStart(3, '0')}-001`,
            invoiceDate: new Date(2024, 0, 15),
            originalAmount: selectedCase.amount * 0.4,
            balanceRemaining: selectedCase.amount * 0.4,
            daysOverdue: selectedCase.daysInCollection,
          },
          {
            id: `${selectedCase.id}-2`,
            headerReference: `INV-2024-${selectedCase.customerId.padStart(3, '0')}-002`,
            invoiceDate: new Date(2024, 1, 1),
            originalAmount: selectedCase.amount * 0.35,
            balanceRemaining: selectedCase.amount * 0.35,
            daysOverdue: Math.max(0, selectedCase.daysInCollection - 15),
          },
          {
            id: `${selectedCase.id}-3`,
            headerReference: `INV-2024-${selectedCase.customerId.padStart(3, '0')}-003`,
            invoiceDate: new Date(2024, 2, 10),
            originalAmount: selectedCase.amount * 0.25,
            balanceRemaining: selectedCase.amount * 0.25,
            daysOverdue: Math.max(0, selectedCase.daysInCollection - 30),
          },
        ] : undefined}
        onSave={(promiseData) => {
          if (!selectedCase) return;
          
          // Update case status to "Promised" and add promise data
          const dataStore = getGlobalDataStore();
          const newPromise: PromiseToPay = {
            id: `PRM-${Date.now()}`,
            customerId: selectedCase.customerId,
            customerName: selectedCase.customerName,
            customerAccountNumber: `ACC-${selectedCase.customerId.padStart(3, '0')}`,
            customerEmail: promiseData.customer.email,
            customerPhone: promiseData.customer.phone,
            caseId: selectedCase.id,
            caseNumber: selectedCase.caseId,
            promiseDate: promiseData.promiseDate,
            promisedAmount: promiseData.promisedAmount,
            status: 'Active',
            confirmationMethod: promiseData.confirmationMethod as 'Phone' | 'Email' | 'Portal',
            createdBy: 'Current User',
            createdAt: new Date(),
            notes: promiseData.notes,
            invoices: [],
            assignedUserId: 0,
            assignedUserName: 'Current User',
            isAtRisk: false,
          };
          
          dataStore.updateCase(selectedCase.caseId, {
            status: 'Promised',
            currentPromise: newPromise,
            promiseHistory: [...(selectedCase.promiseHistory || []), newPromise],
            promiseCount: (selectedCase.promiseCount || 0) + 1,
            nextAction: 'Follow up on promise',
            nextActionDate: promiseData.promiseDate,
          });
          
          // Force re-render by updating state
          window.location.reload();
        }}
      />

      {/* Record Promise Dialog */}
      <RecordPromiseDialog
        open={recordPromiseDialogOpen}
        onOpenChange={setRecordPromiseDialogOpen}
        customerName={selectedCase?.customerName || ''}
        totalAmount={selectedCase?.amount || 0}
        contacts={contactsData}
        onSave={handleRecordPromiseFromQueue}
      />

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />

      {/* Reassign User Modal */}
      <ReassignUserModal
        open={reassignUserOpen}
        onOpenChange={setReassignUserOpen}
        selectedCount={selectedCaseIds.size}
        onSave={handleReassignUserSave}
      />

      {/* Update Status Modal */}
      <UpdateStatusModal
        open={updateStatusOpen}
        onOpenChange={setUpdateStatusOpen}
        selectedCount={selectedCaseIds.size}
        onSave={handleUpdateStatusSave}
      />

    </div>
  );
}
